import React, { useState, useEffect } from 'react';
import {
  Users,
  Phone,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Key,
  Server,
  Database,
  Zap,
  Globe,
  Shield,
  DollarSign,
  Settings,
  Power,
  RefreshCw
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import api from '../services/api';

const AdminDashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [providerOverrides, setProviderOverrides] = useState(null);
  const [togglingProvider, setTogglingProvider] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState(null);
  const [balances, setBalances] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      setDashboard(response.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderOverrides = async () => {
    try {
      const response = await api.get('/admin/provider-overrides');
      setProviderOverrides(response.data);
    } catch (err) {
      console.error('Failed to fetch provider overrides:', err);
    }
  };

  const fetchBalances = async () => {
    try {
      setBalancesLoading(true);
      const response = await api.get('/admin/balances');
      setBalances(response.data);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    } finally {
      setBalancesLoading(false);
    }
  };

  const toggleProvider = async (provider, enabled) => {
    setTogglingProvider(true);
    setOverrideMessage(null);
    try {
      const response = await api.post(`/admin/provider-overrides/${provider}`, {
        enabled: !enabled
      });
      setProviderOverrides(response.data);
      setOverrideMessage({
        success: true,
        message: `${provider} ${!enabled ? 'enabled' : 'disabled'} successfully. Active layer: ${response.data.currentLayer.name}`
      });
      // Refresh dashboard to show updated voice provider health
      await fetchDashboard();
    } catch (err) {
      setOverrideMessage({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to toggle provider'
      });
    } finally {
      setTogglingProvider(false);
    }
  };

  const applyTestScenario = async (scenarioId) => {
    setTogglingProvider(true);
    setOverrideMessage(null);
    try {
      const response = await api.post(`/admin/provider-overrides/scenarios/${scenarioId}`);
      setProviderOverrides(response.data);
      setOverrideMessage({
        success: true,
        message: `Test scenario applied successfully. Active layer: ${response.data.currentLayer.name}`
      });
      await fetchDashboard();
    } catch (err) {
      setOverrideMessage({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to apply scenario'
      });
    } finally {
      setTogglingProvider(false);
    }
  };

  const resetOverrides = async () => {
    setTogglingProvider(true);
    setOverrideMessage(null);
    try {
      const response = await api.post('/admin/provider-overrides/reset');
      setProviderOverrides(response.data);
      setOverrideMessage({
        success: true,
        message: 'All providers reset to normal operation'
      });
      await fetchDashboard();
    } catch (err) {
      setOverrideMessage({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to reset overrides'
      });
    } finally {
      setTogglingProvider(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchProviderOverrides();
    fetchBalances();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboard();
      fetchProviderOverrides();
      fetchBalances();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid':
      case 'configured':
        return 'text-green-600 bg-green-50';
      case 'invalid':
      case 'quota_exceeded':
        return 'text-red-600 bg-red-50';
      case 'not_configured':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-yellow-600 bg-yellow-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
      case 'configured':
        return <CheckCircle className="w-5 h-5" />;
      case 'invalid':
      case 'quota_exceeded':
        return <XCircle className="w-5 h-5" />;
      case 'not_configured':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {return null;}

  const systemHealth = dashboard.systemHealth ?? {};
  const activeSessions = systemHealth.activeSessions ?? 0;
  const totalProcessed = Number(systemHealth.totalProcessed ?? 0);
  const recentErrorsCount = systemHealth.recentErrors ?? 0;
  const errorRate = systemHealth.errorRate ?? 0;
  const uptimeSeconds = systemHealth.uptime ?? 0;
  const memoryUsed = Math.round(systemHealth.memory?.used ?? 0);
  const memoryTotal = Math.round(systemHealth.memory?.total ?? 0);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Customer tracking, API monitoring, and system health
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </div>
          <button
            onClick={fetchDashboard}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Customer Metrics */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Customer Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{dashboard.customers.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active (30 days)</p>
                <p className="text-2xl font-bold text-green-600">{dashboard.customers.active}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-600">{dashboard.customers.inactive}</p>
              </div>
              <Activity className="w-8 h-8 text-gray-600" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plans</p>
                <div className="flex gap-2 mt-1">
                  {dashboard.customers.planDistribution.map(p => (
                    <span key={p.plan} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {p.plan}: {p.count}
                    </span>
                  ))}
                </div>
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Account Balances - Critical for monitoring API costs */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DollarSign className="w-5 h-5 mr-2" />
          Account Balances
          {balancesLoading && <RefreshCw className="w-4 h-4 ml-2 animate-spin text-gray-400" />}
        </h2>

        {/* Critical Alerts Banner */}
        {balances?.hasCriticalIssues && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900">CRITICAL: Low Balance Detected</p>
                <ul className="text-xs text-red-800 mt-1 space-y-1">
                  {balances.criticalIssues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Vonage Balance */}
          <div className={clsx(
            'border-2 rounded-lg p-4',
            balances?.balances?.telephony?.vonage?.critical
              ? 'bg-red-50 border-red-400'
              : balances?.balances?.telephony?.vonage?.lowBalance
                ? 'bg-yellow-50 border-yellow-400'
                : 'bg-white border-gray-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Vonage</h4>
              {balances?.balances?.telephony?.vonage?.critical ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : balances?.balances?.telephony?.vonage?.lowBalance ? (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>
            {balances?.balances?.telephony?.vonage?.balance ? (
              <>
                <p className={clsx(
                  'text-3xl font-bold',
                  balances.balances.telephony.vonage.critical
                    ? 'text-red-600'
                    : balances.balances.telephony.vonage.lowBalance
                      ? 'text-yellow-600'
                      : 'text-green-600'
                )}>
                  {balances.balances.telephony.vonage.currency === 'EUR' ? '\u20AC' : '$'}
                  {balances.balances.telephony.vonage.balance}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {balances.balances.telephony.vonage.currency} Balance
                </p>
                {balances.balances.telephony.vonage.critical && (
                  <p className="text-xs text-red-600 font-semibold mt-2">
                    ADD FUNDS IMMEDIATELY
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">
                {balances?.balances?.telephony?.vonage?.status === 'not_configured'
                  ? 'Not configured'
                  : balances?.balances?.telephony?.vonage?.error || 'Unable to fetch'}
              </p>
            )}
          </div>

          {/* OpenAI */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">OpenAI</h4>
              {balances?.balances?.ai?.openai?.status === 'connected' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-600">
              {balances?.balances?.ai?.openai?.status === 'connected'
                ? 'Connected'
                : balances?.balances?.ai?.openai?.status || 'Unknown'}
            </p>
            <p className="text-xs text-gray-400 mt-2 italic">
              Balance check requires OpenAI dashboard
            </p>
          </div>

          {/* Claude/Anthropic */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Claude AI</h4>
              {balances?.balances?.ai?.claude?.status === 'connected' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-600">
              {balances?.balances?.ai?.claude?.status === 'connected'
                ? 'Connected'
                : balances?.balances?.ai?.claude?.status || 'Unknown'}
            </p>
            <p className="text-xs text-gray-400 mt-2 italic">
              Balance check requires Anthropic console
            </p>
          </div>

          {/* Twilio (if configured) */}
          {balances?.balances?.telephony?.twilio?.status === 'connected' && (
            <div className={clsx(
              'border-2 rounded-lg p-4',
              balances.balances.telephony.twilio.critical
                ? 'bg-red-50 border-red-400'
                : balances.balances.telephony.twilio.lowBalance
                  ? 'bg-yellow-50 border-yellow-400'
                  : 'bg-white border-gray-200'
            )}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Twilio</h4>
                {balances.balances.telephony.twilio.critical ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : balances.balances.telephony.twilio.lowBalance ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <p className={clsx(
                'text-3xl font-bold',
                balances.balances.telephony.twilio.critical
                  ? 'text-red-600'
                  : balances.balances.telephony.twilio.lowBalance
                    ? 'text-yellow-600'
                    : 'text-green-600'
              )}>
                ${balances.balances.telephony.twilio.balance}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {balances.balances.telephony.twilio.currency} Balance
              </p>
            </div>
          )}

          {/* ElevenLabs (if configured with usage) */}
          {balances?.balances?.ai?.elevenlabs?.characterLimit && (
            <div className={clsx(
              'border-2 rounded-lg p-4',
              balances.balances.ai.elevenlabs.percentUsed > 90
                ? 'bg-red-50 border-red-400'
                : balances.balances.ai.elevenlabs.percentUsed > 75
                  ? 'bg-yellow-50 border-yellow-400'
                  : 'bg-white border-gray-200'
            )}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">ElevenLabs</h4>
                {balances.balances.ai.elevenlabs.percentUsed > 90 ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <p className={clsx(
                'text-2xl font-bold',
                balances.balances.ai.elevenlabs.percentUsed > 90
                  ? 'text-red-600'
                  : 'text-gray-900'
              )}>
                {balances.balances.ai.elevenlabs.percentUsed}% Used
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {balances.balances.ai.elevenlabs.remainingCharacters?.toLocaleString()} chars remaining
              </p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className={clsx(
                    'h-2 rounded-full',
                    balances.balances.ai.elevenlabs.percentUsed > 90
                      ? 'bg-red-600'
                      : balances.balances.ai.elevenlabs.percentUsed > 75
                        ? 'bg-yellow-500'
                        : 'bg-green-600'
                  )}
                  style={{ width: `${balances.balances.ai.elevenlabs.percentUsed}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {balances?.balances?.lastChecked && (
          <p className="text-xs text-gray-400 mt-2">
            Last checked: {new Date(balances.balances.lastChecked).toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Call Statistics */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2" />
          Call Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Calls</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.calls.total.toLocaleString()}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Calls Today</p>
            <p className="text-2xl font-bold text-blue-600">{dashboard.calls.today}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">This Week</p>
            <p className="text-2xl font-bold text-purple-600">{dashboard.calls.thisWeek}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Avg Duration</p>
            <p className="text-2xl font-bold text-green-600">
              {Math.round(dashboard.calls.averageDuration)}s
            </p>
          </div>
        </div>

        {/* Language Distribution */}
        {dashboard.calls.languageDistribution.length > 0 && (
          <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <Globe className="w-4 h-4 mr-2" />
              Language Distribution
            </h3>
            <div className="flex gap-3">
              {dashboard.calls.languageDistribution.map(lang => (
                <div key={lang.language} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">{lang.language}:</span>
                  <span className="text-sm text-gray-600">{lang.count} calls</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* API Keys Monitoring */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Key className="w-5 h-5 mr-2" />
          API Keys & Integrations
        </h2>

        {/* Voice AI Providers */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Voice AI Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {['retell', 'openai', 'claude', 'assemblyai'].map(provider => {
              const key = dashboard.apiKeys[provider];
              if (!key) {return null;}

              return (
                <div
                  key={provider}
                  className={clsx(
                    'border rounded-lg p-4 transition-colors',
                    getStatusColor(key.status)
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold capitalize">{provider}</h4>
                    {getStatusIcon(key.status)}
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">Status: {key.status}</p>
                    {key.keyPreview && (
                      <p className="font-mono text-xs text-gray-600">{key.keyPreview}</p>
                    )}
                    {key.note && <p className="text-xs italic">{key.note}</p>}
                    {key.error && <p className="text-xs text-red-600">{key.error}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Other Providers (On Hold) */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Providers (On Hold)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['cartesia', 'elevenlabs'].map(provider => {
              const key = dashboard.apiKeys[provider];
              if (!key) {return null;}

              return (
                <div
                  key={provider}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold capitalize">{provider}</h4>
                    <Activity className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="text-gray-600">Status: {key.status}</p>
                    {key.note && <p className="text-xs italic text-gray-500">{key.note}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* System Keys */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">System Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['vonage_api_key', 'jwt_secret', 'stripe_secret_key'].map(provider => {
              const key = dashboard.apiKeys[provider];
              if (!key) {return null;}

              return (
                <div
                  key={provider}
                  className={clsx(
                    'border rounded-lg p-4',
                    key.configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm">
                      {provider.replace(/_/g, ' ').toUpperCase()}
                    </h4>
                    {key.configured ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {key.configured ? 'Configured' : 'Not configured'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Voice Provider Health */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2" />
          Voice Provider Health (Phase 1 Protection)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className={clsx(
            'border rounded-lg p-4',
            dashboard.voiceProviders.providers.retell
              ? 'bg-green-50 border-green-200'
              : 'bg-yellow-50 border-yellow-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Retell AI</h4>
              {dashboard.voiceProviders.providers.retell ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">Recommended Provider</p>
          </div>

          <div className={clsx(
            'border rounded-lg p-4',
            dashboard.voiceProviders.providers.openai
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">OpenAI Realtime</h4>
              {dashboard.voiceProviders.providers.openai ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">Layer 1 Provider</p>
          </div>

          <div className={clsx(
            'border rounded-lg p-4',
            dashboard.voiceProviders.providers.claude
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Claude AI</h4>
              {dashboard.voiceProviders.providers.claude ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">Fallback Layer 2</p>
          </div>

          <div className={clsx(
            'border rounded-lg p-4',
            dashboard.voiceProviders.providers.assemblyai
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">AssemblyAI</h4>
              {dashboard.voiceProviders.providers.assemblyai ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">STT/TTS Provider</p>
          </div>

          <div className={clsx(
            'border rounded-lg p-4',
            dashboard.voiceProviders.emergencyModeActive
              ? 'bg-red-50 border-red-200'
              : 'bg-green-50 border-green-200'
          )}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Emergency Mode</h4>
              {dashboard.voiceProviders.emergencyModeActive ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">
              {dashboard.voiceProviders.emergencyModeActive ? 'ACTIVE' : 'Standby'}
            </p>
          </div>
        </div>
      </div>

      {/* Provider Override Controls */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Failsafe Testing Controls
          </h2>
          <button
            onClick={fetchProviderOverrides}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh override status"
          >
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Test Mode Warning Banner */}
        {providerOverrides?.testMode && (
          <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-900">TEST MODE ACTIVE</p>
                <p className="text-xs text-yellow-800 mt-1">
                  Provider overrides are in effect. System is using {providerOverrides.currentLayer.name}.
                  Last modified by: {providerOverrides.modifiedBy} at {new Date(providerOverrides.lastModified).toLocaleString()}
                </p>
              </div>
              <button
                onClick={resetOverrides}
                disabled={togglingProvider}
                className="ml-4 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors disabled:opacity-50"
              >
                Reset to Normal
              </button>
            </div>
          </div>
        )}

        {/* Current Layer Status */}
        {providerOverrides && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-900">Current Active Layer</p>
                <p className="text-lg font-bold text-blue-700 mt-1">
                  Layer {providerOverrides.currentLayer.layer}: {providerOverrides.currentLayer.name}
                </p>
              </div>
              {providerOverrides.currentLayer.layer === 4 && (
                <div className="px-3 py-1 bg-red-600 text-white text-sm font-bold rounded">
                  EMERGENCY MODE
                </div>
              )}
            </div>
          </div>
        )}

        {/* Provider Toggle Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {providerOverrides && Object.entries(providerOverrides.overrides).map(([key, provider]) => {
            if (key === 'emergency_ivr') {return null;} // Don't show emergency IVR toggle

            return (
              <div
                key={key}
                className={clsx(
                  'border-2 rounded-lg p-4 transition-all',
                  provider.enabled
                    ? 'bg-green-50 border-green-300'
                    : 'bg-gray-50 border-gray-300'
                )}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Layer {provider.layer}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{provider.description}</p>
                  </div>
                  <button
                    onClick={() => toggleProvider(key, provider.enabled)}
                    disabled={togglingProvider}
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                      provider.enabled
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-400 text-white hover:bg-gray-500',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    <Power className="w-4 h-4" />
                    {provider.enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {provider.enabled ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-700 font-medium">Active</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-600 font-medium">Disabled (Testing)</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Test Scenario Quick Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Test Scenarios</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => applyTestScenario('scenario_1')}
              disabled={togglingProvider}
              className="p-3 border-2 border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-start gap-2 mb-2">
                <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Test Layer 2 Fallback</p>
                  <p className="text-xs text-gray-600 mt-1">Disable Layer 1 (GPT-4o)</p>
                </div>
              </div>
              <p className="text-xs text-blue-700 font-medium">Expected: Claude + AssemblyAI activates</p>
            </button>

            <button
              onClick={() => applyTestScenario('scenario_2')}
              disabled={togglingProvider}
              className="p-3 border-2 border-red-300 rounded-lg hover:bg-red-50 transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Test Emergency IVR</p>
                  <p className="text-xs text-gray-600 mt-1">Disable Layer 1 + Layer 2</p>
                </div>
              </div>
              <p className="text-xs text-red-700 font-medium">Expected: Emergency IVR activates</p>
            </button>

            <button
              onClick={() => applyTestScenario('scenario_3')}
              disabled={togglingProvider}
              className="p-3 border-2 border-green-300 rounded-lg hover:bg-green-50 transition-colors text-left disabled:opacity-50"
            >
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Normal Operation</p>
                  <p className="text-xs text-gray-600 mt-1">Enable all providers</p>
                </div>
              </div>
              <p className="text-xs text-green-700 font-medium">Expected: Layer 1 (GPT-4o) activates</p>
            </button>
          </div>
        </div>

        {/* Override Message */}
        {overrideMessage && (
          <div className={clsx(
            'mt-4 p-4 rounded-lg border',
            overrideMessage.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-start gap-2">
              {overrideMessage.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              )}
              <p className={clsx(
                'text-sm font-medium',
                overrideMessage.success ? 'text-green-900' : 'text-red-900'
              )}>
                {overrideMessage.message}
              </p>
            </div>
          </div>
        )}

        {/* Testing Instructions */}
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Testing Instructions</h4>
          <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
            <li><strong>Layer 1 → Layer 2:</strong> Click &quot;Test Layer 2 Fallback&quot; button, then make a test call. System should use Claude + AssemblyAI.</li>
            <li><strong>Layer 1+2 → Emergency IVR:</strong> Click &quot;Test Emergency IVR&quot; button, then call. System should play DTMF menu for callback collection.</li>
            <li><strong>Verify:</strong> Check the &quot;Current Active Layer&quot; indicator above to confirm the expected layer is active.</li>
            <li><strong>Reset:</strong> Always click &quot;Normal Operation&quot; or &quot;Reset to Normal&quot; when testing is complete.</li>
          </ol>
        </div>
      </div>

      {/* System Health */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Server className="w-5 h-5 mr-2" />
          System Health
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Active Sessions</p>
            <p className="text-2xl font-bold text-blue-600">
              {activeSessions}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Processed</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalProcessed.toLocaleString()}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Recent Errors</p>
            <p className="text-2xl font-bold text-red-600">
              {recentErrorsCount}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Error rate: {errorRate}%
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">System Uptime</p>
            <p className="text-2xl font-bold text-green-600">
              {Math.floor(uptimeSeconds / 3600)}h
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Memory: {memoryUsed}MB / {memoryTotal}MB
            </p>
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Customers by Call Volume</h2>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total Calls
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboard.customers.topCustomers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {customer.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {customer.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={clsx(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      customer.plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                        customer.plan === 'enterprise' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                    )}>
                      {customer.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {customer.totalCalls.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Dashboard generated in {dashboard.generationTime}ms</span>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            <span>Auto-refresh every 30 seconds</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
